import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLButtonElement} [seeMoreButton] - MSH: manual "See more" button (button-driven pagination mode).
 * @property {HTMLButtonElement} [showLessButton] - MSH: manual "Show less" button (button-driven pagination mode).
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // MSH: initialize "See more" / "Show less" button visibility for the
    // button-driven pagination mode. No-op (both refs undefined) when this
    // instance is using native infinite scroll or classic paged pagination.
    this.#updatePaginationButtons();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // MSH: update the URL and sync the "See more" / "Show less" buttons
    // immediately, before yielding to the main thread below. `yieldToMainThread`
    // (scheduler.yield(), see @theme/utilities) exists purely so the browser gets
    // a chance to paint the newly appended cards before we do more work -- it is
    // a performance nicety, not something correctness should depend on. Its
    // continuation is subject to the browser's normal task throttling for
    // backgrounded/unfocused tabs, which can delay it by several seconds or
    // more (confirmed live: identical code, tab visibilityState "hidden", the
    // continuation after this same yield took 3+ seconds to run). Previously
    // both of these calls sat after the yield, so a shopper who clicked
    // "See more" and immediately switched tabs/apps saw the new cards appear
    // but got a stale URL and a "Show less" button stuck hidden until they
    // tabbed back in, exactly the reported bug.
    history.pushState('', '', nextPage.url.toString());
    this.#updatePaginationButtons();

    await yieldToMainThread();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    // MSH: see the matching comment in #renderNextPage. Moved ahead of the
    // yield below so the URL update isn't subject to background-tab task
    // throttling on the yield's continuation.
    history.pushState('', '', previousPage.url.toString());

    await yieldToMainThread();

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * MSH: public entry point for the "See more" button (button-driven pagination
   * mode, see snippets/product-grid.liquid `pagination_style == 'button'`).
   *
   * `#renderNextPage` is private and therefore unreachable from the declarative
   * `on:click="/seeMore"` attribute the Component base class uses to wire up
   * clicks (it resolves plain string method names via `instance[method]`, which
   * cannot see true `#`-private class fields). This wrapper is the minimal
   * public surface needed to reuse the existing, correct fetch/append/history
   * logic in `#renderNextPage` instead of reimplementing it.
   *
   * @param {Event} [event]
   */
  async seeMore(event) {
    event?.preventDefault();
    await this.#renderNextPage();
    // MSH: the primary button-visibility sync now happens inside
    // #renderNextPage itself, immediately after the URL update and before the
    // (possibly throttled) yield -- see the comment there. This second call is
    // a cheap, idempotent defensive re-sync for after the full render settles.
    this.#updatePaginationButtons();
  }

  /**
   * MSH: public entry point for the "Show less" button (button-driven
   * pagination mode). Collapses the grid back to page 1 and scrolls back to
   * the top of the grid. There is no equivalent native behaviour to reuse
   * here (infinite scroll never removes loaded pages), so this is new logic.
   *
   * @param {Event} [event]
   */
  showLess(event) {
    event?.preventDefault();

    // MSH 2026-09-16 (May: "see less buttons sometimes gets lost"): when the page was
    // opened straight at ?page=N, page 1 is not in the grid, so collapsing would empty
    // it. Go back to the collection instead of doing nothing.
    const { grid } = this.refs;
    const hasFirstPage = grid?.querySelector(':scope > [ref="cards[]"][data-page="1"]');
    if (grid && !hasFirstPage) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.hash = '';
      window.location.href = url.toString();
      return;
    }

    this.#collapseToFirstPage();
    this.#updatePaginationButtons();
  }

  /**
   * MSH: removes every card whose `data-page` is greater than 1 from the grid,
   * resets the URL back to page 1 (mirroring the `history.pushState` calls in
   * `#renderNextPage`/`#renderPreviousPage`), and scrolls smoothly back to the
   * top of the grid.
   *
   * Only acts when page 1 is actually present in the grid. A merchant landing
   * directly on a deep link like `?page=3` only has page 3 server-rendered
   * (page 1 is never fetched unless the shopper scrolls/clicks their way back
   * to it), so collapsing in that state would empty the grid. `#updatePaginationButtons`
   * keeps the "Show less" button hidden in that situation for the same reason.
   */
  #collapseToFirstPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const firstPageCard = grid.querySelector(':scope > [ref="cards[]"][data-page="1"]');
    if (!firstPageCard) return;

    for (const card of grid.querySelectorAll(':scope > [ref="cards[]"]')) {
      if (Number(/** @type {HTMLElement} */ (card).dataset.page) > 1) {
        card.remove();
      }
    }

    // MSH 2026-09-16 (May: "when they click see more or see less, it shouldnt jump back
    // to the top"): deliberately no scrollTo here - the shopper keeps their place and
    // only the extra cards are removed. The browser clamps the scroll position by
    // itself if the page has become shorter than the current offset.

    const url = new URL(window.location.href);
    url.searchParams.set('page', '1');
    url.hash = '';
    history.pushState('', '', url.toString());
  }

  /**
   * MSH: syncs the "See more" / "Show less" button visibility with what is
   * currently loaded in the grid. Safe to call regardless of pagination mode:
   * when `seeMoreButton`/`showLessButton` refs don't exist (infinite scroll or
   * classic paged pagination), this is a cheap no-op.
   *
   * Deliberately queries the grid directly instead of going through
   * `this.refs.cards` / `#getPage`: `refs` is refreshed by a MutationObserver
   * that only runs after the current synchronous script finishes, but
   * `showLess` calls this method immediately after synchronously removing
   * cards from the DOM (`#collapseToFirstPage`), with no `await` in between.
   * Reading `this.refs` at that point would still see the just-removed cards.
   */
  #updatePaginationButtons() {
    const { seeMoreButton, showLessButton, grid } = this.refs;
    if (!seeMoreButton && !showLessButton) return;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const cardPages = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).map(
      (card) => Number(/** @type {HTMLElement} */ (card).dataset.page) || 0
    );
    const currentMaxPage = cardPages.length ? Math.max(...cardPages) : 1;
    const hasFirstPage = cardPages.includes(1);

    // MSH 2026-09-16: also show it when the grid starts past page 1 (deep link), where
    // showLess navigates back to the collection instead of collapsing.
    const currentMinPage = cardPages.length ? Math.min(...cardPages) : 1;
    if (showLessButton) showLessButton.hidden = !((hasFirstPage && currentMaxPage > 1) || currentMinPage > 1);
    if (seeMoreButton) seeMoreButton.hidden = currentMaxPage >= lastPage;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');

        // MSH: the filtered result set always starts back at page 1, so
        // re-sync "See more" / "Show less" visibility (no-op outside
        // button-driven pagination mode).
        this.#updatePaginationButtons();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
